/**
 * Heuristic "smart filters" for customer revenue admin (no external LLM).
 * Surfaces high-signal slices from the current Stripe subscription mirror.
 */

export type BillingSubscriptionInsightRow = {
  subscription_id: string;
  tier_id?: string;
  status?: string;
  cancel_at_period_end?: boolean;
  current_period_end?: string;
  created_at?: string;
  updated_at?: string;
  entitlement_sync_status?: string;
  company_name?: string;
  email?: string;
};

export type BillingSmartFilter = {
  id: string;
  label: string;
  reason: string;
  count: number;
  confidence: 'high' | 'medium';
  patch: {
    status?: string;
    plan?: string;
    timeframe?: string;
    sync?: string;
    endingSoon?: boolean;
    cancelScheduled?: boolean;
  };
};

const DAY_MS = 24 * 60 * 60 * 1000;

const parseDate = (value?: string): number | null => {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
};

const tierKey = (tierId?: string) => {
  const tier = String(tierId || '').toLowerCase();
  if (tier === 'advanced') return 'advanced';
  if (tier === 'enterprise') return 'enterprise';
  if (tier === 'basic' || tier === 'complicore' || tier === 'essentials') return 'basic';
  return tier || 'unknown';
};

const tierLabel = (key: string) => {
  if (key === 'advanced') return 'CompliAdvantage';
  if (key === 'enterprise') return 'CompliComplete';
  if (key === 'basic') return 'CompliCore';
  return key;
};

export function buildBillingSmartFilters(
  rows: BillingSubscriptionInsightRow[],
  nowMs = Date.now(),
): BillingSmartFilter[] {
  if (!rows.length) return [];

  const suggestions: BillingSmartFilter[] = [];
  const byPlan: Record<string, number> = {};
  const byStatus: Record<string, number> = {};
  const bySync: Record<string, number> = {};
  let endingSoon = 0;
  let cancelScheduled = 0;
  let renewingThisMonth = 0;
  let createdLast7 = 0;
  let createdLast30 = 0;

  for (const row of rows) {
    const plan = tierKey(row.tier_id);
    byPlan[plan] = (byPlan[plan] || 0) + 1;
    const status = String(row.status || 'unknown').toLowerCase();
    byStatus[status] = (byStatus[status] || 0) + 1;
    const sync = String(row.entitlement_sync_status || 'unknown').toLowerCase();
    bySync[sync] = (bySync[sync] || 0) + 1;
    if (row.cancel_at_period_end) cancelScheduled += 1;

    const periodEnd = parseDate(row.current_period_end);
    if (periodEnd != null) {
      const delta = periodEnd - nowMs;
      if (delta >= 0 && delta <= 7 * DAY_MS) endingSoon += 1;
      const endDate = new Date(periodEnd);
      const now = new Date(nowMs);
      if (endDate.getUTCFullYear() === now.getUTCFullYear() && endDate.getUTCMonth() === now.getUTCMonth()) {
        renewingThisMonth += 1;
      }
    }

    const created = parseDate(row.created_at) ?? parseDate(row.updated_at);
    if (created != null) {
      const age = nowMs - created;
      if (age <= 7 * DAY_MS) createdLast7 += 1;
      if (age <= 30 * DAY_MS) createdLast30 += 1;
    }
  }

  const total = rows.length;
  const dominantPlan = Object.entries(byPlan).sort((a, b) => b[1] - a[1])[0];
  if (dominantPlan && dominantPlan[1] >= Math.max(2, Math.ceil(total * 0.4))) {
    suggestions.push({
      id: `plan-${dominantPlan[0]}`,
      label: `Plan: ${tierLabel(dominantPlan[0])}`,
      reason: `${dominantPlan[1]} of ${total} customers on this plan`,
      count: dominantPlan[1],
      confidence: dominantPlan[1] / total >= 0.6 ? 'high' : 'medium',
      patch: { plan: dominantPlan[0] },
    });
  }

  for (const [status, count] of Object.entries(byStatus)) {
    if (status === 'active' || status === 'unknown') continue;
    if (count <= 0) continue;
    suggestions.push({
      id: `status-${status}`,
      label: `Status: ${status}`,
      reason: `${count} subscription${count === 1 ? '' : 's'} currently ${status}`,
      count,
      confidence: status === 'past_due' || status === 'unpaid' ? 'high' : 'medium',
      patch: { status },
    });
  }

  if (cancelScheduled > 0) {
    suggestions.push({
      id: 'cancel-scheduled',
      label: 'Canceling at period end',
      reason: `${cancelScheduled} active cancel schedule${cancelScheduled === 1 ? '' : 's'} — retention risk`,
      count: cancelScheduled,
      confidence: 'high',
      patch: { cancelScheduled: true },
    });
  }

  if (endingSoon > 0) {
    suggestions.push({
      id: 'ending-soon',
      label: 'Renews in 7 days',
      reason: `${endingSoon} renewal${endingSoon === 1 ? '' : 's'} due within a week`,
      count: endingSoon,
      confidence: 'high',
      patch: { endingSoon: true },
    });
  }

  if (renewingThisMonth > 0 && renewingThisMonth !== endingSoon) {
    suggestions.push({
      id: 'renew-month',
      label: 'Renews this month',
      reason: `${renewingThisMonth} period end${renewingThisMonth === 1 ? '' : 's'} fall in the current month`,
      count: renewingThisMonth,
      confidence: 'medium',
      patch: { timeframe: 'renew_this_month' },
    });
  }

  if (createdLast7 > 0) {
    suggestions.push({
      id: 'new-7',
      label: 'New last 7 days',
      reason: `${createdLast7} recent signup${createdLast7 === 1 ? '' : 's'}`,
      count: createdLast7,
      confidence: 'medium',
      patch: { timeframe: 'created_7d' },
    });
  } else if (createdLast30 > 0) {
    suggestions.push({
      id: 'new-30',
      label: 'New last 30 days',
      reason: `${createdLast30} signup${createdLast30 === 1 ? '' : 's'} in the last month`,
      count: createdLast30,
      confidence: 'medium',
      patch: { timeframe: 'created_30d' },
    });
  }

  const syncProblems = (bySync.dunning || 0) + (bySync.revoked || 0) + (bySync.pending || 0) + (bySync.error || 0);
  if (syncProblems > 0) {
    suggestions.push({
      id: 'sync-attention',
      label: 'Entitlement sync attention',
      reason: `${syncProblems} row${syncProblems === 1 ? '' : 's'} not in a clean synced state`,
      count: syncProblems,
      confidence: 'high',
      patch: { sync: 'attention' },
    });
  }

  return suggestions
    .sort((a, b) => {
      const conf = (b.confidence === 'high' ? 2 : 1) - (a.confidence === 'high' ? 2 : 1);
      if (conf !== 0) return conf;
      return b.count - a.count;
    })
    .slice(0, 8);
}

export function rowMatchesBillingFilters(
  row: BillingSubscriptionInsightRow,
  filters: {
    status: string;
    plan: string;
    timeframe: string;
    sync: string;
    endingSoon: boolean;
    cancelScheduled: boolean;
    query: string;
  },
  nowMs = Date.now(),
): boolean {
  const status = String(row.status || '').toLowerCase();
  if (filters.status !== 'all' && status !== filters.status) return false;

  const plan = tierKey(row.tier_id);
  if (filters.plan !== 'all' && plan !== filters.plan) return false;

  if (filters.cancelScheduled && !row.cancel_at_period_end) return false;

  if (filters.endingSoon) {
    const periodEnd = parseDate(row.current_period_end);
    if (periodEnd == null) return false;
    const delta = periodEnd - nowMs;
    if (delta < 0 || delta > 7 * DAY_MS) return false;
  }

  if (filters.timeframe === 'renew_this_month') {
    const periodEnd = parseDate(row.current_period_end);
    if (periodEnd == null) return false;
    const endDate = new Date(periodEnd);
    const now = new Date(nowMs);
    if (endDate.getUTCFullYear() !== now.getUTCFullYear() || endDate.getUTCMonth() !== now.getUTCMonth()) {
      return false;
    }
  }

  if (filters.timeframe === 'created_7d' || filters.timeframe === 'created_30d') {
    const created = parseDate(row.created_at) ?? parseDate(row.updated_at);
    if (created == null) return false;
    const maxAge = filters.timeframe === 'created_7d' ? 7 * DAY_MS : 30 * DAY_MS;
    if (nowMs - created > maxAge) return false;
  }

  if (filters.timeframe === 'renew_7d') {
    const periodEnd = parseDate(row.current_period_end);
    if (periodEnd == null) return false;
    const delta = periodEnd - nowMs;
    if (delta < 0 || delta > 7 * DAY_MS) return false;
  }

  if (filters.timeframe === 'renew_30d') {
    const periodEnd = parseDate(row.current_period_end);
    if (periodEnd == null) return false;
    const delta = periodEnd - nowMs;
    if (delta < 0 || delta > 30 * DAY_MS) return false;
  }

  const sync = String(row.entitlement_sync_status || '').toLowerCase();
  if (filters.sync === 'attention') {
    if (!['dunning', 'revoked', 'pending', 'error', 'cancel_scheduled'].includes(sync) && !row.cancel_at_period_end) {
      return false;
    }
  } else if (filters.sync !== 'all' && sync !== filters.sync) {
    return false;
  }

  const needle = filters.query.trim().toLowerCase();
  if (needle) {
    const hay = [
      row.company_name,
      row.email,
      row.subscription_id,
      row.tier_id,
      row.entitlement_sync_status,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    if (!hay.includes(needle)) return false;
  }

  return true;
}
